import { useMemo, useSyncExternalStore } from "react";
import { z } from "zod";

/**
 * メモの下書き(ブラウザの localStorage)。入力のたびに自動で保存し、閉じてしまっても残す。
 * 下書きは複数残せる: 新しいメモは「メモを追加」のたびに別の下書き(`new:<id>`)になり、
 * 既存のメモの下書きはメモごとに 1 つ(`memo:<メモ ID>`)。
 * 復元は手動(一覧の「下書き」を選ぶ、または開いたときに「復元 / 破棄」を本人が選ぶ)。
 * このブラウザにだけ残り、サーバーには送らない。使えない環境(プライベートモード等)では黙って何もしない。
 */
export const draftSchema = z.object({
  title: z.string(),
  body: z.string(),
  /** アイコン(2026-10-15 に足した。以前の下書きには無い)。 */
  icon: z.string().optional(),
  /** 下書きを書き始めたときのメモの revision。新規は null。その後に更新されたかの判定に使う。 */
  baseRevision: z.number().nullable(),
  savedAt: z.number(),
});
export type MemoDraft = z.infer<typeof draftSchema>;

export interface StoredDraft extends MemoDraft {
  draftId: string;
}

const PREFIX = "portal:memo-draft:v2:";
const LEGACY_PREFIX = "portal:memo-draft:v1:"; // 下書きが 1 メモ 1 件だった頃のキー

const keyOf = (email: string, draftId: string) => `${PREFIX}${email}:${draftId}`;

/** 新しいメモ用の下書き ID。 */
export function newDraftId(): string {
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `new:${id}`;
}
/** 既存のメモの下書き ID。 */
export const draftIdOfMemo = (memoId: string) => `memo:${memoId}`;
export const isNewDraft = (draftId: string) => draftId.startsWith("new:");
/** 既存のメモの下書きなら、そのメモの ID。 */
export const memoIdOfDraft = (draftId: string) =>
  draftId.startsWith("memo:") ? draftId.slice("memo:".length) : null;

// ---- 変更の通知(一覧が下書きの増減に追従するため)。ほかのタブの変更は storage イベントで拾う
let version = 0;
const listeners = new Set<() => void>();
const notify = () => {
  version++;
  listeners.forEach((l) => l());
};
export function subscribeDrafts(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener("storage", notify);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", notify);
  };
}
export const getDraftsVersion = () => version;

export function readDraft(email: string, draftId: string): MemoDraft | null {
  try {
    const raw = window.localStorage.getItem(keyOf(email, draftId));
    if (!raw) return null;
    const parsed = draftSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function writeDraft(email: string, draftId: string, draft: MemoDraft): void {
  try {
    window.localStorage.setItem(keyOf(email, draftId), JSON.stringify(draft));
    notify();
  } catch {
    // 容量超過・保存不可。下書きは補助機能なので、失敗しても編集は続けられる
  }
}

export function clearDraft(email: string, draftId: string): void {
  try {
    window.localStorage.removeItem(keyOf(email, draftId));
    notify();
  } catch {
    // 同上
  }
}

/** そのユーザーの下書きすべて。保存の新しい順。旧形式(1 メモ 1 件)のキーは、ここで新形式へ移す。 */
export function listDrafts(email: string): StoredDraft[] {
  try {
    const storage = window.localStorage;
    const legacy = `${LEGACY_PREFIX}${email}:`;
    for (const key of Object.keys(storage).filter((k) => k.startsWith(legacy))) {
      const suffix = key.slice(legacy.length);
      const moved = suffix === "new" ? "new:legacy" : draftIdOfMemo(suffix);
      const raw = storage.getItem(key);
      if (raw && !storage.getItem(keyOf(email, moved))) storage.setItem(keyOf(email, moved), raw);
      storage.removeItem(key);
    }
    const mine = `${PREFIX}${email}:`;
    const out: StoredDraft[] = [];
    for (const key of Object.keys(storage).filter((k) => k.startsWith(mine))) {
      const parsed = draftSchema.safeParse(JSON.parse(storage.getItem(key) ?? "null"));
      if (parsed.success) out.push({ ...parsed.data, draftId: key.slice(mine.length) });
    }
    return out.sort((a, b) => b.savedAt - a.savedAt);
  } catch {
    return [];
  }
}

/** ログイン中のユーザーの下書き一覧。保存・破棄に追従して更新される。 */
export function useMemoDrafts(email: string | null): StoredDraft[] {
  const v = useSyncExternalStore(subscribeDrafts, getDraftsVersion);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- v は一覧を読み直す合図
  return useMemo(() => (email ? listDrafts(email) : []), [email, v]);
}
